import * as React from 'react';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { ProjectPropertyType } from 'src/constants/project';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { layoutAlgorithmSetup } from 'src/containers/ProjectDiagram/templates/extensions/layouts/LayoutAlgorithm';
import { redrawLayout } from 'src/containers/ProjectDiagram/templates/extensions/layouts/utils/redrawLayout';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { clearDiagramUndoHistory } from 'src/containers/ProjectDiagram/utils/clearDiagramUndoHistory';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setAllPropertyTypes } from 'src/containers/ProjectDiagram/utils/propertyType/setAllPropertyTypes';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import { useProjectBaseSettings } from 'src/containers/ProjectSettingsDrawer/hooks/useProjectBaseSettingsValue';
import { useProjectModelSettings } from 'src/containers/ProjectSettingsDrawer/hooks/useProjectModelSettingsValue';
import { projectSelector } from 'src/selectors/project';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useSaveProjectSettings = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const project = useReduxSelector(projectSelector(projectId));
  const projectModels = useReduxSelector(projectModelsByIdSelector(projectId));

  const {
    dataNamespace,
    description,
    disableVoiceboxSuggestions,
    name,
    projectLayout,
    propertyType,
    handleResetBaseSettings,
  } = useProjectBaseSettings();
  const {
    handleResetModelSettings,
    modelNamespaces,
    modelPrefixes,
    shouldPublishPrefixByModel,
    sortedModelIds,
    voiceboxInstructionsByModel,
  } = useProjectModelSettings();

  return React.useCallback(async () => {
    await dispatch(async (wrappedDispatch) => {
      wrappedDispatch(
        projectsActionCreators.clearedProjectVoiceboxEvaluation({
          id: projectId,
        })
      );

      if (project?.description !== description) {
        wrappedDispatch(
          projectsActionCreators.updatedProjectDescription(
            projectId,
            description
          )
        );
      }

      if (project?.dataNamespace !== dataNamespace) {
        wrappedDispatch(
          projectsActionCreators.updatedProjectDataNamespace(
            projectId,
            dataNamespace
          )
        );
      }

      if (
        Boolean(project?.disableVoiceboxSuggestions) !==
        disableVoiceboxSuggestions
      ) {
        wrappedDispatch(
          projectsActionCreators.updatedProjectDisableVoiceboxSuggestions(
            projectId,
            disableVoiceboxSuggestions
          )
        );
      }

      if (project?.propertyType !== propertyType) {
        wrappedDispatch(
          projectsActionCreators.updatedProjectPropertyType(
            projectId,
            propertyType
          )
        );

        if (diagram && propertyType !== ProjectPropertyType.CUSTOM) {
          // overwrite custom properties
          commitDiagramModel(diagram, setAllPropertyTypes(propertyType));

          // clear undo history because project settings can't be undone
          clearDiagramUndoHistory(diagram, projectId);
        }
      }

      sortedModelIds.forEach((modelId) => {
        const modelNamespace = modelNamespaces[modelId];
        const projectModel = projectModels[modelId];

        if (!projectModel) {
          return;
        }

        const shouldUpdate =
          projectModel.namespace !== modelNamespace ||
          projectModel.prefix !== modelPrefixes[modelId] ||
          projectModel.shouldPublishPrefix !==
            shouldPublishPrefixByModel[modelId] ||
          (projectModel.voiceboxInstructions || '') !==
            (voiceboxInstructionsByModel[modelId] || '');

        if (shouldUpdate) {
          wrappedDispatch(
            projectsActionCreators.updatedProjectModel(projectId, modelId, {
              namespace: modelNamespace,
              prefix: modelPrefixes[modelId],
              shouldPublishPrefix: shouldPublishPrefixByModel[modelId],
              voiceboxInstructions:
                voiceboxInstructionsByModel[modelId] || undefined,
            })
          );
        }
      });

      if (
        diagram &&
        diagram.model.modelData.layoutAlgorithm !== projectLayout
      ) {
        // Persist the chosen layout algorithm on the model (works headless).
        commitDiagramModel(diagram, (model) => {
          setDataProperty<ModelData>(model)(
            model.modelData,
            'layoutAlgorithm',
            projectLayout
          );
        });

        // Applying + redrawing the layout is canvas-only
        if (!diagram.headless) {
          // having these in the same commit didn't run the layout
          commitDiagramModel(
            diagram,
            (model, diagram) => {
              // eslint-disable-next-line no-param-reassign
              diagram.layout = layoutAlgorithmSetup(
                model.modelData.layoutAlgorithm
              );

              redrawLayout(model, diagram);
            },
            { layout: DiagramModelCommitLayout.FULL }
          );
        }

        // clear undo history because project settings can't be undone
        clearDiagramUndoHistory(diagram, projectId);
      }
    });

    if (project?.name !== name) {
      // must wait for previous action middleware to finish completely
      // before renaming project to avoid cloud sync issues
      dispatch(projectsActionCreators.renamedProject({ id: projectId, name }));
    }

    handleResetBaseSettings();
    handleResetModelSettings();
    dispatch(uiSlice.actions.toggledProjectSettingsDrawer({ isOpen: false }));
  }, [
    dataNamespace,
    description,
    diagram,
    disableVoiceboxSuggestions,
    dispatch,
    handleResetBaseSettings,
    handleResetModelSettings,
    modelNamespaces,
    modelPrefixes,
    name,
    project,
    projectId,
    projectLayout,
    projectModels,
    propertyType,
    shouldPublishPrefixByModel,
    sortedModelIds,
    voiceboxInstructionsByModel,
  ]);
};
